// Path: src/context/TimerContext.tsx
// Resolved merge conflict by adopting HEAD’s unified notification with Loader, Progress, and concise text
// Maintained session time (40:00–45:00 Egypt time), 5-min warning at 35:00, 10-sec warning at 39:50
// Ensured isSessionActive drives green border vibration in layouts
// Fixed notification closure on Safari with dynamic notificationKey
// Shortened notification text: "seconds" to "s", "remaining" to "re"
// Added debug logs for verification
// 06/01/25 05:11 PM +07
// Updated session timing to 00:15–00:20 Egypt time
// Enlarged notification with stacked Loader and Progress
// Removed 'Join OLS' link per user preference
// Added debouncing to reduce re-renders
// Fixed notification rendering for Safari
// 06/02/25 10:05 AM +07
// Fixed 'useMemo is not defined' by adding import
// 06/02/25 10:13 AM +07
// Fixed ESLint warnings: Added useMemo and useEffect dependencies
// Ensured Loader visibility with explicit display and z-index
// 06/02/25 11:07 AM +07
// Path: src/context/TimerContext.tsx
// Fixed slow and frozen timer by reducing debounce to 500ms and ensuring state reset post-session
// Aligned sessionProgress with elapsed seconds to prevent inconsistencies
// Added debug logs for timer state
// 06/02/25 4:25 PM +07
// Path: src/context/TimerContext.tsx
// Fixed sessionProgress stuck at 290 by resetting to 0 when session is inactive
// 06/02/25 5:37 PM +07
// Path: src/context/TimerContext.tsx
// Fixed ESLint warning by adding intervalLabel to useMemo dependency array
// 06/03/25 5:08 PM +07

import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from "react";
import { notification, Progress } from "antd";
import { DateTime } from "luxon";
import { debounce } from "lodash";
import Loader from "../components/Loader";

interface OlsmeTimerData {
  timer: string;
  sessionLabel: string;
  nextSession: string;
  isSessionActive: boolean;
  sessionProgress: number;
  intervalLabel: string;
}

interface TimerContextType {
  timerData: OlsmeTimerData;
}

const TimerContext = createContext<TimerContextType | undefined>(undefined);

export const TimerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [timer, setTimer] = useState<string>("");
  const [sessionLabel, setSessionLabel] = useState<string>("");
  const [nextSession, setNextSession] = useState<string>("");
  const [isSessionActive, setIsSessionActive] = useState<boolean>(false);
  const [hasWarnedFiveMinutes, setHasWarnedFiveMinutes] = useState<boolean>(false);
  const [hasWarnedTenSeconds, setHasWarnedTenSeconds] = useState<boolean>(false);
  const [hasNotifiedSessionStart, setHasNotifiedSessionStart] = useState<boolean>(false);
  const [sessionProgress, setSessionProgress] = useState<number>(0);
  const [intervalLabel, setIntervalLabel] = useState<string>("Charge In");
  const [isNotificationOpen, setIsNotificationOpen] = useState<boolean>(false);
  const notificationKey = useRef(`olsmeInProgress-${Date.now()}`);

  const updateTimer = useMemo(
    () =>
      debounce(() => {
        const egyptTime = DateTime.now().setZone("Africa/Cairo");
        const localTime = DateTime.now();

        const hours = egyptTime.hour;
        const minutes = egyptTime.minute;
        const seconds = egyptTime.second;

        let nextSessionTime = egyptTime.set({ hour: hours, minute: 15, second: 0, millisecond: 0 });
        if (minutes >= 15) {
          nextSessionTime = egyptTime.set({ hour: hours + 1, minute: 15, second: 0, millisecond: 0 });
        }

        let sessionEndTime = egyptTime.set({ hour: hours, minute: 20, second: 0, millisecond: 0 });
        if (minutes >= 15 && minutes < 20) {
          sessionEndTime = egyptTime.set({ minute: 20, second: 0, millisecond: 0 });
        } else if (minutes >= 20) {
          sessionEndTime = nextSessionTime.set({ minute: 20, second: 0, millisecond: 0 });
        }

        const nextSessionFormatted = nextSessionTime
          .setZone(localTime.zoneName)
          .toLocaleString(DateTime.TIME_SIMPLE);
        setNextSession(nextSessionFormatted);

        const isDuringSession = minutes >= 15 && minutes < 20;
        setIsSessionActive(isDuringSession);
        console.log("Verification: Timer updated", { isDuringSession, minutes, seconds, sessionProgress });

        if (isDuringSession) {
          const timeDiff = sessionEndTime.diff(egyptTime, "seconds").seconds;
          const minutesLeft = Math.floor(timeDiff / 60);
          const secondsLeft = Math.floor(timeDiff % 60);
          setTimer(`${minutesLeft}:${secondsLeft < 10 ? "0" : ""}${secondsLeft}`);

          const sessionStartTime = egyptTime.set({ minute: 15, second: 0, millisecond: 0 });
          const elapsedSeconds = Math.floor(egyptTime.diff(sessionStartTime, "seconds").seconds);
          const progress = Math.min(Math.max(elapsedSeconds, 0), 300);
          setSessionProgress(progress);

          const interval = Math.floor(progress / 100);
          setIntervalLabel(interval === 0 ? "Charge In" : interval === 1 ? "Expand Out" : "Be In");

          if (minutes === 15 && seconds === 0 && !hasNotifiedSessionStart) {
            notification.open({
              key: notificationKey.current,
              message: `OLSME Session: ${intervalLabel}`,
              description: (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "10px" }}>
                  <div style={{ background: "rgba(255, 170, 0, 0.2)", borderRadius: "50%", padding: "10px", marginBottom: "20px", display: "block", zIndex: 1 }}>
                    <Loader style={{ transform: "scale(2)" }} />
                  </div>
                  <Progress percent={(progress / 300) * 100} showInfo={false} strokeColor="#00ff00" trailColor="#666" style={{ width: "90%", marginBottom: "15px" }} />
                  <div style={{ color: "#fff", fontSize: "18px", textAlign: "center", fontWeight: "bold" }}>
                    {300 - progress} s re ({Math.round((progress / 300) * 100)}%)
                  </div>
                </div>
              ),
              placement: "topRight",
              duration: 0,
              style: { background: "linear-gradient(#333, #1a1a1a)", borderRadius: "16px", boxShadow: "0 0 20px rgba(0, 255, 0, 0.8)", padding: "30px", width: "500px", minHeight: "300px", zIndex: 1000 },
              onClick: () => console.log("Verification: Notification clicked", { progress, intervalLabel }),
            });
            setHasNotifiedSessionStart(true);
            setIsNotificationOpen(true);
          }

          if (isNotificationOpen && (minutes >= 15 && minutes < 20)) {
            notification.open({
              key: notificationKey.current,
              message: `OLSME Session: ${intervalLabel}`,
              description: (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "10px" }}>
                  <div style={{ background: "rgba(255, 170, 0, 0.2)", borderRadius: "50%", padding: "10px", marginBottom: "20px", display: "block", zIndex: 1 }}>
                    <Loader style={{ transform: "scale(2)" }} />
                  </div>
                  <Progress percent={(progress / 300) * 100} showInfo={false} strokeColor="#00ff00" trailColor="#666" style={{ width: "90%", marginBottom: "15px" }} />
                  <div style={{ color: "#fff", fontSize: "18px", textAlign: "center", fontWeight: "bold" }}>
                    {300 - progress} s re ({Math.round((progress / 300) * 100)}%)
                  </div>
                </div>
              ),
              placement: "topRight",
              duration: 0,
              style: { background: "linear-gradient(#333, #1a1a1a)", borderRadius: "16px", boxShadow: "0 0 20px rgba(0, 255, 0, 0.8)", padding: "30px", width: "500px", minHeight: "300px", zIndex: 1000 },
              onClick: () => console.log("Verification: Notification updated", { progress, intervalLabel }),
            });
          }

          if (minutes === 20 && seconds === 0 && isNotificationOpen) {
            notification.close(notificationKey.current);
            setIsNotificationOpen(false);
            setHasWarnedFiveMinutes(false);
            setHasWarnedTenSeconds(false);
            setHasNotifiedSessionStart(false);
            setSessionProgress(0);
            setIntervalLabel("Charge In");
            notificationKey.current = `olsmeInProgress-${Date.now()}`;
          }
        } else {
          if (isNotificationOpen) {
            notification.close(notificationKey.current);
            setIsNotificationOpen(false);
            setHasNotifiedSessionStart(false);
          }

          const timeDiff = nextSessionTime.diff(egyptTime, "seconds").seconds;
          const minutesLeft = Math.floor(timeDiff / 60);
          const secondsLeft = Math.floor(timeDiff % 60);
          setTimer(`${minutesLeft}:${secondsLeft < 10 ? "0" : ""}${secondsLeft}`);
          setSessionLabel("until ‘olsme’ session");

          // Reset sessionProgress when session is not active
          if (sessionProgress !== 0) {
            setSessionProgress(0);
            setIntervalLabel("Charge In");
          }

          if (minutes === 10 && seconds === 0 && !hasWarnedFiveMinutes) {
            notification.warning({
              message: "OLSME Alert",
              description: `5 minutes until the next OLSME session begins at ${nextSessionFormatted} (your local time)!`,
              placement: "topRight",
              duration: 300,
            });
            setHasWarnedFiveMinutes(true);
          } else if (minutes !== 10 || seconds !== 0) {
            setHasWarnedFiveMinutes(false);
          }

          if (minutes === 14 && seconds === 50 && !hasWarnedTenSeconds) {
            notification.warning({
              message: "OLSME Alert",
              description: "Prepare OLSME session beginning in 10 s!",
              placement: "topRight",
              duration: 10,
            });
            setHasWarnedTenSeconds(true);
          } else if (minutes !== 14 || seconds !== 50) {
            setHasWarnedTenSeconds(false);
          }
        }
      }, 500),
    [hasNotifiedSessionStart, hasWarnedFiveMinutes, hasWarnedTenSeconds, isNotificationOpen, sessionProgress, intervalLabel] // Added intervalLabel
  );

  useEffect(() => {
    const timerInterval = setInterval(updateTimer, 500);
    updateTimer();

    return () => {
      clearInterval(timerInterval);
      if (isNotificationOpen) {
        notification.close(notificationKey.current);
        setIsNotificationOpen(false);
      }
      console.log("Verification: TimerContext cleanup completed");
    };
  }, [isSessionActive, isNotificationOpen, updateTimer]);

  const timerData: OlsmeTimerData = {
    timer,
    sessionLabel,
    nextSession,
    isSessionActive,
    sessionProgress,
    intervalLabel,
  };

  return <TimerContext.Provider value={{ timerData }}>{children}</TimerContext.Provider>;
};

export const useTimer = (): OlsmeTimerData => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error("useTimer must be used within a TimerProvider");
  }
  return context.timerData;
};

export default TimerContext;